/**
 * Card head (headline + arrows top-right) and the day columns of the
 * "A · Columns" grid. Owns the hover/focus fade, swipe paging and the slide
 * animation; all derivation lives in rescheduleSlotModel.
 */
import { FC, useRef, useState } from "react"
import { Animated, Easing, GestureResponderEvent, TextStyle, View, ViewStyle } from "react-native"

import { Text } from "@/components/Text"

import { DayColumn } from "./DayColumn"
import { LiveDot } from "./LiveDot"
import { prefersReducedMotion } from "./motion"
import { NavArrow } from "./NavArrow"
import type { SlotGridView } from "./rescheduleSlotModel"
import { font, SLIDE_MS, SLIDE_OFFSET, SWIPE_THRESHOLD, tokens } from "./tokens"

export interface SlotGridProps {
  view: SlotGridView
  expanded: boolean
  onPrev: () => void
  onNext: () => void
  onSelect: (date: string, time: string) => void
  onRetryDay: (date: string) => void
}

type Direction = 1 | -1

/** Fade + slide in from the side the customer paged towards. */
function useSlide() {
  const progress = useRef(new Animated.Value(1)).current
  const [dir, setDir] = useState<Direction>(1)
  const run = (next: Direction) => {
    if (prefersReducedMotion()) return
    setDir(next)
    progress.setValue(0)
    Animated.timing(progress, {
      toValue: 1,
      duration: SLIDE_MS,
      easing: Easing.out(Easing.quad),
      useNativeDriver: false,
    }).start()
  }
  const style = {
    opacity: progress,
    transform: [
      {
        translateX: progress.interpolate({
          inputRange: [0, 1],
          outputRange: [dir * SLIDE_OFFSET, 0],
        }),
      },
    ],
  }
  return { run, style }
}

export const SlotGrid: FC<SlotGridProps> = ({
  view,
  expanded,
  onPrev,
  onNext,
  onSelect,
  onRetryDay,
}) => {
  const slide = useSlide()
  const [hovered, setHovered] = useState<string | null>(null)
  const [focused, setFocused] = useState<string | null>(null)
  const touchStartX = useRef<number | null>(null)
  // Hover wins over focus; with neither, nothing fades (touch-only devices).
  const activeDate = hovered ?? focused

  const goPrev = () => {
    if (!view.canPrev) return
    slide.run(-1)
    onPrev()
  }
  const goNext = () => {
    if (!view.canNext) return
    slide.run(1)
    onNext()
  }

  const onTouchStart = (e: GestureResponderEvent) => {
    touchStartX.current = e.nativeEvent.touches[0]?.pageX ?? null
  }
  const onTouchEnd = (e: GestureResponderEvent) => {
    const start = touchStartX.current
    touchStartX.current = null
    const end = e.nativeEvent.changedTouches[0]?.pageX
    if (start === null || end === undefined) return
    const dx = end - start
    if (Math.abs(dx) <= SWIPE_THRESHOLD) return
    if (dx < 0) goNext()
    else goPrev()
  }

  return (
    <View style={$card}>
      <View style={$head}>
        <View style={$headline}>
          <LiveDot />
          <Text style={$headlineText} testID="grid-headline">
            {view.headline ?? ""}
          </Text>
        </View>
        <View style={$arrows}>
          <NavArrow
            direction="left"
            label="Frühere Tage"
            disabled={!view.canPrev}
            onPress={goPrev}
          />
          <NavArrow
            direction="right"
            label="Spätere Tage"
            disabled={!view.canNext}
            onPress={goNext}
          />
        </View>
      </View>
      <Animated.View
        style={[$columns, slide.style]}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        testID="grid-columns"
      >
        {view.columns.map((col, i) => (
          <DayColumn
            key={col.date}
            col={col}
            hasPrevious={i > 0}
            recommendedTime={view.recommended?.date === col.date ? view.recommended.time : null}
            expanded={expanded}
            dimmed={activeDate !== null && activeDate !== col.date}
            active={activeDate === col.date}
            onHoverIn={() => setHovered(col.date)}
            onHoverOut={() => setHovered((d) => (d === col.date ? null : d))}
            onSlotFocus={() => setFocused(col.date)}
            onSlotBlur={() => setFocused((d) => (d === col.date ? null : d))}
            onSelect={onSelect}
            onRetry={onRetryDay}
          />
        ))}
      </Animated.View>
    </View>
  )
}

const $card: ViewStyle = { gap: 16 }
const $head: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
}
const $headline: ViewStyle = {
  flex: 1,
  minWidth: 0,
  flexDirection: "row",
  alignItems: "center",
  gap: 8,
}
const $headlineText: TextStyle = {
  flex: 1,
  fontSize: 16,
  lineHeight: 20,
  fontFamily: font.bold,
  color: tokens.ink,
}
const $arrows: ViewStyle = { flexDirection: "row", gap: 6, flexShrink: 0 }
const $columns: ViewStyle = { flexDirection: "row", gap: 12 }
